import React, { useEffect, useRef, useState } from 'react';

// C3-b tenant-degradation console (UXeng Step-5 contract). ONE surface, cause-detection gates
// which state renders; precedence outage > cap > ok is server-authoritative. Reused at
// top-of-shell in the agent console and (later) in the C4 wizard Test step.
interface InferenceStatus {
  state: 'ok' | 'outage' | 'cap';
  usage: { used: number; limit: number; windowResetsAt: string };
}

const NEAR_CAP = 0.8; // frontend-derived; backend stays 3 states
const POLL_MS = 45000;

// Analytics stub — real pipeline wires later; the event fires regardless (per UXeng).
const track = (event: string, props?: Record<string, unknown>) => {
  try { window.dispatchEvent(new CustomEvent('analytics', { detail: { event, props } })); } catch { /* noop */ }
  // eslint-disable-next-line no-console
  console.log('[analytics]', event, props || '');
};

// The quota_exhausted / near-cap upgrade CTA lands on the Phase-2 plans page, where the tenant
// picks a tier → eng-1's Stripe test-mode checkout. Full-nav (not react-router) since this
// component mounts outside any one router context (top-of-shell + wizard Test step).
const goUpgrade = () => {
  track('upgrade_cta_click');
  window.location.href = '/plans';
};

export const InferenceStatusBanner: React.FC = () => {
  const [status, setStatus] = useState<InferenceStatus | null>(null);
  const [dismissedNearCap, setDismissedNearCap] = useState(false);
  const quotaFired = useRef(false);

  const fetchStatus = async () => {
    try {
      const res = await fetch('/api/account/inference-status');
      if (res.ok) setStatus(await res.json());
    } catch { /* keep last known */ }
  };

  useEffect(() => {
    fetchStatus();
    const id = setInterval(fetchStatus, POLL_MS);
    // Immediate refetch when a live-chat inference error fires, so the banner appears in
    // seconds rather than on the next poll.
    const onErr = () => fetchStatus();
    window.addEventListener('inference:error', onErr as EventListener);
    return () => { clearInterval(id); window.removeEventListener('inference:error', onErr as EventListener); };
  }, []);

  if (!status) return null;
  const { state, usage } = status;

  // OUTAGE — neutral/info, NO CTA (never upsell during our own outage).
  if (state === 'outage') {
    return (
      <Bar testid="inference-banner-outage" className="bg-slate-700/80 text-slate-100 border-slate-500">
        Your AI is temporarily unavailable — we’re on it. No action needed.
      </Bar>
    );
  }

  // CAP — prominent persistent + primary upgrade CTA; fire quota_exhausted once on render.
  if (state === 'cap') {
    if (!quotaFired.current) { quotaFired.current = true; track('quota_exhausted'); }
    return (
      <Bar testid="inference-banner-cap" className="bg-red-900/40 text-red-100 border-red-700">
        You’ve reached your plan’s conversation limit — upgrade to keep your agent answering.
        <button onClick={goUpgrade} className="ml-3 px-3 py-1 rounded bg-red-600 hover:bg-red-500 text-white text-xs font-bold">Upgrade</button>
      </Bar>
    );
  }

  // OK + near-cap (>=80%) — amber, dismissible, secondary upgrade link.
  const ratio = usage.limit > 0 ? usage.used / usage.limit : 0;
  if (usage.limit > 0 && ratio >= NEAR_CAP && !dismissedNearCap) {
    return (
      <Bar testid="inference-banner-nearcap" className="bg-amber-900/30 text-amber-100 border-amber-700/60">
        You’ve used {usage.used}/{usage.limit} conversations this period — <button onClick={goUpgrade} className="underline hover:no-underline">upgrade for more</button>.
        <button onClick={() => setDismissedNearCap(true)} aria-label="Dismiss" className="ml-3 text-amber-300/70 hover:text-amber-100">✕</button>
      </Bar>
    );
  }

  return null; // ok & under threshold → hidden
};

const Bar: React.FC<{ testid: string; className?: string; children: React.ReactNode }> = ({ testid, className, children }) => (
  <div data-testid={testid} className={`w-full px-4 py-2 text-sm border-b flex items-center justify-center text-center ${className || ''}`}>
    {children}
  </div>
);

export default InferenceStatusBanner;
